import { useEffect } from 'react';
import { getLocation, subscribeLocation } from '../lib/engagement';
import { flushSeatEngagement } from '../lib/admin-reporter';

// 무입력(자리비움) 임계값 — 이 시간 동안 입력이 없으면 측정을 멈춰 "켜두고 자리비움"을
// 체류시간에서 제외한다. (useTimeTracker 와 동일 정책)
const IDLE_MS = 90 * 1000;
// 좌석 하트비트 주기. 모니터의 "접속 중" 판정(마지막 하트비트 < 60s)보다 짧아야 한다.
const FLUSH_MS = 30 * 1000;

// 위치 id 를 Firestore 맵 키/필드패스에 안전한 형태로 정규화 (점/콜론 등 제거).
const keyOf = (id) => (id || 'home').replace(/[^A-Za-z0-9_-]/g, '_');

/**
 * 플랫폼 전역 engagement 추적기 — 학생 앱 루트에 1회 마운트.
 * Day 개념과 무관하게 "이 플랫폼에 얼마나 머물렀나(활성)", "지금/주로 어디 있나",
 * "접속 중인가"를 좌석 문서에 라이브로 기록한다.
 *
 * 누적값은 increment(델타)로 더한다 — 세션마다 0으로 덮어쓰면 이전 누적이 사라진다.
 * 측정 제외: 백그라운드 탭(document.hidden), 무입력 자리비움(IDLE_MS).
 * 기관 좌석(CLS-) 이 아니면 flushSeatEngagement 가 자동 no-op.
 */
export default function useEngagement(enabled = true) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    let activeMs = 0;        // 이번 세션 누적(활성)
    let flushedMs = 0;       // 이미 좌석에 increment 로 반영한 양
    const locMs = {};        // key -> 이번 세션 누적 ms
    const locFlushed = {};   // key -> 이미 반영한 ms
    const locLabel = {};     // key -> 표시 라벨
    let lastStart = null;
    let loc = getLocation();
    let idleTimer = null;
    let flushTimer = null;

    const accumulate = () => {
      if (lastStart == null) return;
      const delta = Date.now() - lastStart;
      activeMs += delta;
      const k = keyOf(loc.id);
      locMs[k] = (locMs[k] || 0) + delta;
      locLabel[k] = loc.label;
      lastStart = Date.now();
    };
    const start = () => {
      if (lastStart != null || document.hidden) return;
      lastStart = Date.now();
    };
    const pause = () => {
      accumulate();
      lastStart = null;
    };
    const flush = () => {
      accumulate();
      const deltaMs = activeMs - flushedMs;
      const locDeltas = {};
      Object.keys(locMs).forEach((k) => {
        const d = locMs[k] - (locFlushed[k] || 0);
        if (d > 0) locDeltas[k] = { ms: d, label: locLabel[k] };
      });
      // 반영 완료로 마킹
      flushedMs = activeMs;
      Object.keys(locMs).forEach((k) => { locFlushed[k] = locMs[k]; });
      flushSeatEngagement({ deltaMs, locId: loc.id, locLabel: loc.label, locDeltas });
    };
    const armIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => { pause(); flush(); }, IDLE_MS);
    };
    const onActivity = () => {
      if (!document.hidden) start();
      armIdle();
    };
    const onVis = () => {
      if (document.hidden) {
        pause();
        flush();
        if (idleTimer) clearTimeout(idleTimer);
      } else {
        start();
        armIdle();
      }
    };
    const onLoc = (next) => {
      accumulate();           // 지금까지의 시간을 이전 위치에 귀속
      loc = next;             // 위치 전환
      locLabel[keyOf(loc.id)] = loc.label;
      if (lastStart == null && !document.hidden) start();
      flush();                // 즉시 반영 → 모니터의 "현재 위치" 라이브 갱신
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    if (!document.hidden) { start(); armIdle(); }
    document.addEventListener('visibilitychange', onVis);
    activityEvents.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    const unsubLoc = subscribeLocation(onLoc);
    window.addEventListener('beforeunload', flush);
    flushTimer = setInterval(flush, FLUSH_MS);
    flush(); // 진입 즉시 접속 표시 + 현재 위치 기록 (deltaMs=0 이라 누적엔 영향 없음)

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      activityEvents.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener('beforeunload', flush);
      unsubLoc();
      if (idleTimer) clearTimeout(idleTimer);
      if (flushTimer) clearInterval(flushTimer);
      pause();
      flush();
    };
  }, [enabled]);
}
